import "core-js/modules/web.dom-collections.iterator.js";
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/* eslint-disable react/destructuring-assignment */
import React, { useRef, useState, useLayoutEffect, useEffect, useMemo, forwardRef } from 'react';
import { styled } from '@mui/material';
import InputAdornment from "@mui/material/InputAdornment";
import AutoSelect from '../AutoSelect';
import CircularProgress from '../CircularProgress';
import FormInputLayout from "../FormInputLayout";
import { useDebounce } from '../utils';
const AsyncList = styled(AutoSelect)(() => ({
  display: "inherit !important",
  '& .MuiAutocomplete-input': {
    width: 'auto !important'
  }
}));
const ListBoxComponent = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    loadingNextPage,
    renderDropdownLoading,
    renderDropdownAdornment,
    children,
    ...restProps
  } = _ref;
  return /*#__PURE__*/React.createElement("ul", _extends({}, restProps, {
    ref: ref
  }), children, Boolean(renderDropdownAdornment) && renderDropdownAdornment, loadingNextPage && (renderDropdownLoading || /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'center',
      padding: '15px'
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: 17
  }))));
});
export default function AutoSelectAsyncList(props) {
  const {
    inputValue: propsInputValue = '',
    disableCloseOnSelect = true,
    debounceInput
  } = props;
  const debounceInputTime = useMemo(() => {
    if (debounceInput === true) {
      return 400;
    }
    if (!debounceInput) {
      return 0;
    }
    if (debounceInput) {
      return debounceInput;
    }
    return 0;
  }, [debounceInput]);
  const [inputValue, setInputValue] = useState('');
  const scrollBoxRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const lastPageLength = useRef(0);
  const debouncedSearchTerm = useDebounce(inputValue, debounceInputTime);
  const handleListBoxScroll = event => {
    const listboxNode = event.currentTarget;
    scrollPositionRef.current = listboxNode.scrollTop;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight && !props.loadingNextPage && typeof props.onFetchNextPage === 'function' && props.options.length !== lastPageLength.current) {
      props.onFetchNextPage(props.currentPage + 1);
      lastPageLength.current = props.options.length;
    }
  };
  const handleClose = () => {
    scrollPositionRef.current = 0;
    if (typeof props.onClose === 'function') {
      props.onClose();
    }
  };
  useEffect(() => {
    setInputValue(propsInputValue);
  }, [propsInputValue]);
  useEffect(() => {
    if (typeof props.onDebouncedKeyStroke === 'function') {
      props.onDebouncedKeyStroke(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  // when the new page results come back we want to maintain where the user was scrolled in the list
  useLayoutEffect(() => {
    if (scrollBoxRef.current) {
      scrollBoxRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [props.options.length]);

  // when the user starts typing we want to scroll them back up to the top of the list
  useLayoutEffect(() => {
    if (scrollBoxRef.current) {
      scrollBoxRef.current.scrollTop = 0;
    }
    scrollPositionRef.current = 0;
    lastPageLength.current = 0;
  }, [inputValue]);
  return /*#__PURE__*/React.createElement(FormInputLayout, null, /*#__PURE__*/React.createElement(AsyncList, {
    id: props.id
    // @ts-ignore
    ,
    spacing: 2,
    multiple: props.multiple,
    error: props.error,
    required: props.required,
    label: props.fieldLabel,
    placeholder: props.placeholder,
    loading: props.loading || props.loadingNextPage,
    ListboxProps: {
      ref: scrollBoxRef,
      onScroll: handleListBoxScroll,
      loadingNextPage: props.loadingNextPage,
      renderDropdownLoading: props.renderDropdownLoading,
      renderDropdownAdornment: props.renderDropdownAdornment
    },
    onChange: (event, value, reason, details) => {
      if (typeof props.onChange === 'function') {
        props.onChange(event, value, reason, details);
      }
      lastPageLength.current = 0;
    },
    onClose: handleClose,
    inputProps: {
      onChange: event => {
        setInputValue(event.target.value);
        if (typeof props.onKeyStroke === 'function') {
          props.onKeyStroke(event, event.target.value);
        }
      }
    },
    inputValue: inputValue,
    value: props.value,
    options: props.options,
    isOptionEqualToValue: props.isOptionEqualToValue,
    getOptionLabel: props.getOptionLabel,
    getValueLabel: props.getValueLabel,
    filterOptions: props.filterOptions,
    disableCloseOnSelect: disableCloseOnSelect,
    loadingText: props.loadingText,
    disabled: props.disabled,
    fullWidth: props.fullWidth,
    endAdornment: (props.loading || props.loadingNextPage) && /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end"
    }, props.renderLoadingAdornment ? props.renderLoadingAdornment : /*#__PURE__*/React.createElement(CircularProgress, {
      size: 17
    })),
    ListboxComponent: ListBoxComponent,
    renderTags: props.renderTags,
    renderOption: props.renderOption
  }));
}